/**
 * Work queue — "Việc cần xử lý" (subsystems/identity/facade/adapters/admin-api/src/admin/work_items.rs).
 *
 * One item per underlying problem that needs a person. Review kinds are decided with approve /
 * reject (dossier.review); exception kinds are closed with a reason. Items whose problem goes
 * away are AUTO_RESOLVED by the system. Items carry codes and statuses only — never identity
 * numbers or names.
 */

import type { CallOptions, ISODateTime, RecordedReason, WriteOptions } from '../types';
import type { DossierQueueApi } from './dossierQueue';
import type { PostOperationsApi } from './postOperations';

export const REVIEW_KINDS = [
  'EKYC_MISMATCH',
  'EKYC_FAILED',
  'VERIFICATION_INCONCLUSIVE',
  'DOSSIER_MANUAL_REVIEW',
  'SUBJECT_CHANGED_AFTER_DOSSIER',
  'VNNIC_SYNC_MISSING',
  'VNNIC_SYNC_STUCK',
  'VNNIC_SYNC_REJECTED',
  'VNNIC_SYNC_DRIFT',
  'DECLARATION_REVIEW',
  // A registration of a manual-activation .vn zone (gov.vn, edu.vn) waiting before the registry create.
  'MANUAL_ACTIVATION',
] as const;

export const EXCEPTION_KINDS = [
  'WORKFLOW_FAILED',
  'WORKFLOW_MANUAL_REVIEW',
  'REGISTRY_TRANSACTION',
  'RECONCILIATION_ISSUE',
  'VNNIC_LIST_MISMATCH',
  'COVERAGE_GAP',
  'NOTIFICATION_FAILED',
  'WEBHOOK_ENDPOINT_DISABLED',
] as const;

/** Decided on their name-change request or VNNIC panel operation (never closed as "done" here). */
export const REQUEST_KINDS = [
  'HOLDER_TRANSFER_REVIEW',
  'SUBJECT_CONVERSION_REVIEW',
  'NAME_CORRECTION_REVIEW',
  'VNNIC_PANEL_NAME_CHANGE',
  'VNNIC_PANEL_REGISTRY_LOCK',
  'VNNIC_PANEL_REGISTRY_UNLOCK',
] as const;

/** Protected domains and overdue panel operations (closed with a reason, or by the system); what an
 * add-on asks staff to look at (ADDON_ATTENTION, in the add-on's own words). */
export const OTHER_KINDS = ['PROTECTED_CHANGE_PENDING', 'PROTECTED_DOMAIN_EXPIRY', 'PROTECTED_TRANSFER_OUT', 'VNNIC_PANEL_OVERDUE', 'REGISTRAR_SIGNATURE_MISMATCH', 'REGISTRAR_SIGNATURE_PRECHECK', 'POST_OPERATION_ATTENTION', 'ADDON_ATTENTION', 'EINVOICE_RUN_FAILED'] as const;

export const WORK_KINDS = [...REVIEW_KINDS, ...REQUEST_KINDS, ...EXCEPTION_KINDS, ...OTHER_KINDS] as const;
export type WorkKind = (typeof WORK_KINDS)[number];
export const WORK_SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
export type WorkSeverity = (typeof WORK_SEVERITIES)[number];
export type WorkStatus = 'OPEN' | 'RESOLVED' | 'AUTO_RESOLVED';
export type WorkResolution = 'APPROVED' | 'REJECTED' | 'CORRECTION_REQUESTED' | 'DONE' | 'AUTO_RESOLVED';
export type ReviewDecision = 'APPROVE' | 'REJECT';

export const isReviewKind = (k: WorkKind): boolean => (REVIEW_KINDS as readonly string[]).includes(k);
export const isRequestKind = (k: WorkKind): boolean => (REQUEST_KINDS as readonly string[]).includes(k);

/** Identity review items: an eKYC mismatch / failure, an inconclusive verification. */
export const IDENTITY_KINDS: readonly WorkKind[] = ['EKYC_MISMATCH', 'EKYC_FAILED', 'VERIFICATION_INCONCLUSIVE'];

/**
 * Whether staff may approve the item. Owner decision 2026-10-01: an individual is verified only
 * by VNNIC eKYC, an organisation by its subject declaration. An identity item, or an individual's
 * registration declaration, is rejected / sent back for correction, never approved (the backend
 * answers STAFF_VERIFICATION_REMOVED / INDIVIDUAL_EKYC_ONLY). An organisation's paper
 * declaration (subject declaration, or the domain declaration when it cannot sign digitally) is
 * approved by staff after reading the OCR report; a parental-consent or return form may still be
 * accepted.
 */
export const canApprove = (item: { kind: WorkKind; detail?: WorkDetail | null }): boolean => {
  if (IDENTITY_KINDS.includes(item.kind)) return false;
  if (item.kind === 'DECLARATION_REVIEW') {
    const purpose = typeof item.detail?.purpose === 'string' ? item.detail.purpose : 'REGISTRATION';
    const individual = item.detail?.subject_type === 'INDIVIDUAL' || item.detail?.subject_type === undefined;
    return purpose !== 'REGISTRATION' || !individual;
  }
  return true;
};

/** Per-field comparison of the eKYC provider (never the values). */
export type FieldResult = 'MATCH' | 'MISMATCH' | 'UNREADABLE' | 'NOT_DECLARED';

export interface WorkDetail {
  reason_codes?: string[];
  comparison?: Partial<Record<'name' | 'document_number' | 'birth', FieldResult>> | null;
  face?: { band?: string | null; similarity?: string | null; threshold?: string | null };
  changed?: string[];
  [key: string]: unknown;
}

export interface WorkItem {
  id: string;
  kind: WorkKind;
  severity: WorkSeverity;
  status: WorkStatus;
  source_kind: string;
  source_ref: string;
  summary: string;
  detail: WorkDetail;
  opened_by: string;
  opened_at: ISODateTime;
  assigned_to: string | null;
  assigned_to_display_name: string | null;
  assigned_at: ISODateTime | null;
  first_action_at: ISODateTime | null;
  resolved_at: ISODateTime | null;
  resolved_by: string | null;
  resolved_by_display_name: string | null;
  resolution: WorkResolution | null;
  resolution_reason: RecordedReason | null;
  resolution_detail: Record<string, unknown> | null;
  domain_id: string | null;
  domain_name: string | null;
  unicode_name: string | null;
  lifecycle_state: string | null;
  dossier_id: string | null;
  dossier_status: string | null;
  dossier_version: number | null;
  subject_id: string | null;
  subject_version_id: string | null;
  verification_id: string | null;
  verification_status: string | null;
  verification_method: string | null;
  /** A compliance deadline (e.g. the 10-day review of a name change). */
  due_at?: ISODateTime | null;
}

export interface WorkQuery {
  /** `CLOSED` = RESOLVED or AUTO_RESOLVED. */
  status?: WorkStatus | 'CLOSED';
  kind?: WorkKind;
  severity?: WorkSeverity;
  /** `me`, `unassigned` or a staff id. */
  assignee?: string;
  min_age_hours?: number;
  domain_id?: string;
  subject_id?: string;
  /** `ops`: only operations kinds (OPS_KINDS); `work`: everything else. */
  category?: 'ops' | 'work';
  limit?: number;
}

export interface WorkList {
  items: WorkItem[];
  open_by_kind: Partial<Record<WorkKind, number>>;
  open_by_severity: Partial<Record<WorkSeverity, number>>;
  open_unassigned: number;
  open_mine: number;
}

export interface ResolveWorkBody {
  /** Review kinds only. */
  decision?: ReviewDecision;
  reason: string;
  /** Review kinds, on REJECT: shown to the registrant. */
  customer_note?: string;
}

export interface Assignee {
  id: string;
  display_name: string;
  email: string;
}

/** Operations noise, hidden from the work queue by default ("Vận hành" filter). */
export const OPS_KINDS = [
  'REGISTRY_TRANSACTION',
  'RECONCILIATION_ISSUE',
  'VNNIC_LIST_MISMATCH',
  'COVERAGE_GAP',
  'NOTIFICATION_FAILED',
  'WEBHOOK_ENDPOINT_DISABLED',
  'WORKFLOW_FAILED',
  'WORKFLOW_MANUAL_REVIEW',
] as const satisfies readonly WorkKind[];
export const isOpsKind = (k: WorkKind): boolean => (OPS_KINDS as readonly string[]).includes(k);

export interface WorkApi extends DossierQueueApi, PostOperationsApi {
  listWorkItems(query: WorkQuery, opts?: CallOptions): Promise<WorkList>;
  getWorkItem(id: string, opts?: CallOptions): Promise<WorkItem>;
  listWorkAssignees(opts?: CallOptions): Promise<{ items: Assignee[] }>;
  assignWorkItem(id: string, body: { assignee_id?: string }, opts?: WriteOptions): Promise<{ id: string; assigned_to: string }>;
  resolveWorkItem(id: string, body: ResolveWorkBody, opts?: WriteOptions): Promise<{ item_id: string; resolution: WorkResolution; detail: Record<string, unknown> }>;
}
